<template>
	<StepperItems
		:step="step"
		:steps="steps"
	>
		<template
			v-if="step === 0"
			#0
		>
			<IntegrationServiceDetails
				:user-full-permissions="userFullPermissions"
				:is-edit="isEdit"
				:system-integration-items="systemIntegrationItems"
				:brand-id="integrationService.brandId"
				:integration-id="integrationService.integrationId"
				:has-template="hasTemplate"
				@update:brandId="updateBrandId"
				@update:integrationId="updateIntegrationService('integrationId', $event)"
			/>
		</template>
		<template
			v-if="step === 1"
			#1
		>
			<IntegrationServiceData
				:user-full-permissions="userFullPermissions"
				:is-edit="isEdit"
				:clone-mode="cloneMode"
				:cassie-object-id.sync="integrationService.cassieObjectId"
				:object-id-column="integrationService.objectIdColumn"
				:trigger="integrationService.trigger"
				:reset-incremental-value="integrationService.resetIncrementalValue"
				:update-stand-dak="integrationService.updateStandDak"
				:object-select-list="integrationService.objectSelectList"
				:object-filter-clause="integrationService.objectFilterClause"
				:client-target-object-name="integrationService.clientTargetObjectName"
				:target-system-action="integrationService.targetSystemAction"
				:is-enabled="integrationService.isEnabled"
				:returned-value="integrationService.returnedValue"
				:cassie-objects="cassieObjects"
				:data-type-items="dataTypeItems"
				:target-system-action-items="targetSystemActionItems"
				:output-filters="outputFilters"
				:integration-filters="integrationService.filters"
				:object-client-urn-column="integrationService.objectClientUrnColumn"
				@update:cassieObjectId="updateIntegrationService('cassieObjectId', $event)"
				@update:objectIdColumn="updateIntegrationService('objectIdColumn', $event)"
				@update:resetIncrementalValue="updateIntegrationService('resetIncrementalValue', $event)"
				@update:updateStandDak="updateIntegrationService('updateStandDak', $event)"
				@update:objectSelectList="updateIntegrationService('objectSelectList', $event)"
				@update:objectFilterClause="updateIntegrationService('objectFilterClause', $event)"
				@update:clientTargetObjectName="updateIntegrationService('clientTargetObjectName', $event)"
				@update:targetSystemAction="updateIntegrationService('targetSystemAction', $event)"
				@update:isEnabled="updateIntegrationService('isEnabled', $event)"
				@update:returnedValue="updateIntegrationService('returnedValue', $event)"
				@update:objectClientUrnColumn="updateIntegrationService('objectClientUrnColumn', $event)"
				@update:filters="updateIntegrationService('filters', $event)"
			/>
		</template>
		<template
			v-if="step === 2"
			#2
		>
			<IntegrationServiceConnection
				:user-full-permissions="userFullPermissions"
				:is-edit="isEdit"
				:http-method="integrationService.httpMethod"
				:authentication-url="integrationService.authenticationUrl"
				:request-body-format="integrationService.requestBodyFormat"
				:request-url="integrationService.requestUrl"
				:request-body="integrationService.requestBody"
				:request-throttling-wait-time-m-s="integrationService.requestThrottlingWaitTimeMS"
				:custom-headers="integrationService.customHeaders"
				:http-method-items="httpMethodItems"
				:cassie-objects="cassieObjects"
				:cassie-object-id="integrationService.cassieObjectId"
				:selected-fields="selectedFieldsForConnection"
				@update:httpMethod="updateIntegrationService('httpMethod', $event)"
				@update:authenticationUrl="updateIntegrationService('authenticationUrl', $event)"
				@update:requestBodyFormat="updateIntegrationService('requestBodyFormat', $event)"
				@update:requestUrl="updateIntegrationService('requestUrl', $event)"
				@update:requestBody="updateIntegrationService('requestBody', $event)"
				@update:requestThrottlingWaitTimeMS="updateIntegrationService('requestThrottlingWaitTimeMS', $event)"
				@update:customHeaders="updateIntegrationService('customHeaders', $event)"
			/>
		</template>
		<template
			v-if="step === 3"
			#3
		>
			<IntegrationServiceConnectionResponse
				:user-full-permissions="userFullPermissions"
				:is-edit="isEdit"
				:response-body-format="integrationService.responseBodyFormat"
				:update-cassie-contacts="integrationService.updateStandDak"
				:success-responses="integrationService.successResponses"
				:retry-failed-messages="integrationService.retryFailedMessages"
				:data-type-items="dataTypeItems"
				:cassie-object-id="integrationService.cassieObjectId"
				:selected-fields="selectedFieldsForConnection"
				:object-client-urn-column="integrationService.objectClientUrnColumn"
				:returned-value="integrationService.returnedValue"
				:returned-value-mappings="integrationService.returnedValueMappings"
				:cassie-objects="lookupData.cassieObjects"
				@update:responseBodyFormat="updateIntegrationService('responseBodyFormat', $event)"
				@update:successResponses="updateIntegrationService('successResponses', $event)"
				@update:retryFailedMessages="updateIntegrationService('retryFailedMessages', $event)"
				@update:mappingsBatch="updateMappingsBatch"
				@update:updateCassieContacts="updateIntegrationService('updateStandDak', $event)"
			/>
		</template>
	</StepperItems>
</template>

<script>
import { mapGetters } from 'vuex'
import StepperItems from '../../../../../../shared/components/stepper-items.vue'
import IntegrationServiceDetails from './integration-service-details.vue'
import IntegrationServiceConnection from './integration-service-connection.vue'
import IntegrationServiceConnectionResponse from './integration-service-connection-response.vue'
import IntegrationServiceData from './integration-service-data.vue'

export default {
	components: { StepperItems, IntegrationServiceDetails, IntegrationServiceConnection, IntegrationServiceConnectionResponse, IntegrationServiceData },
	props: {
		step: Number,
		steps: Array,
		integrationService: Object,
		lookupData: Object,
		systemIntegrationItems: Array,
		cloneMode: Boolean,
		hasTemplate: Boolean,
		userFullPermissions: {
			type: Boolean,
			default: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		isEdit () {
			return !!this.integrationService.id && !this.cloneMode
		},
		httpMethodItems () {
			return this.lookupData?.httpMethods
		},
		targetSystemActionItems () {
			return this.lookupData?.targetSystemActions
		},
		urnColumnItems () {
			return this.lookupData?.urnColumns
		},
		cassieObjects () {
			return this.lookupData?.cassieObjects
		},
		dataTypeItems () {
			return this.lookupData?.cassieObjects.map(({ cassieObjectId, cassieObjectName, isUpdateStanddak }) => ({
				text: cassieObjectName,
				value: cassieObjectId,
				isUpdateStanddak: isUpdateStanddak
			}))
		},
		outputFilters () {
			return this.lookupData?.outputFilters
		},
		selectedFieldsForConnection () {
			// Get the current Cassie object to get field details
			const currentObject = this.cassieObjects?.find(obj => obj.cassieObjectId === this.integrationService.cassieObjectId)
			if (!currentObject || !currentObject.cassieObjectFields) {
				return []
			}

			let selectedFields = []

			// Use objectSelectList (the fields actually selected by the user in step 1)
			if (this.integrationService.objectSelectList && this.integrationService.objectSelectList.length > 0) {
				// Map field names to full field objects
				selectedFields = this.integrationService.objectSelectList.map(fieldName => {
					const field = currentObject.cassieObjectFields.find(f => f.cassieObjectFieldName === fieldName)
					if (field) {
						return {
							fieldName: field.cassieObjectFieldName,
							displayName: field.cassieObjectFieldDisplayName || field.cassieObjectFieldName,
							typeFriendlyName: field.typeFriendlyName || field.systemTypeName || 'Text',
							dataType: field.dataType || 'string',
							isFilterable: field.isFilterable || false,
							isIncremental: field.isIncremental || false
						}
					}
					return null
				}).filter(Boolean) // Remove any null entries
			}

			// Always ensure the incremental field is included if it exists
			const incrementalField = currentObject.cassieObjectFields.find(field => field.isIncremental)
			if (incrementalField && !selectedFields.find(field => field.fieldName === incrementalField.cassieObjectFieldName)) {
				selectedFields.push({
					fieldName: incrementalField.cassieObjectFieldName,
					displayName: incrementalField.cassieObjectFieldDisplayName || incrementalField.cassieObjectFieldName,
					typeFriendlyName: incrementalField.typeFriendlyName || incrementalField.systemTypeName || 'Text',
					dataType: incrementalField.dataType || 'string',
					isFilterable: incrementalField.isFilterable || false,
					isIncremental: incrementalField.isIncremental || false
				})
			}

			return selectedFields
		}
	},
	methods: {
		async clearBrandIdRelatedFields () {
			await this.$nextTick()
		},
		updateIntegrationService (property, value) {
			this.$emit('update:integrationService', {
				...this.integrationService,
				[property]: value
			})
		},
		/**
		 * Updates multiple mapping-related properties atomically in a single emit.
		 * This prevents race conditions where sequential emits overwrite each other
		 * due to stale prop state (bug #23695).
		 * @param {Object} batch - Object containing returnedValueMappings, objectClientUrnColumn, returnedValue
		 */
		updateMappingsBatch (batch) {
			this.$emit('update:integrationService', {
				...this.integrationService,
				returnedValueMappings: batch.returnedValueMappings,
				objectClientUrnColumn: batch.objectClientUrnColumn,
				returnedValue: batch.returnedValue
			})
		},
		async updateBrandId (value) {
			this.updateIntegrationService('brandId', value)
			await this.$nextTick()
			this.clearBrandIdRelatedFields()
		}
	}
}
</script>
